import React from 'react';
import { fmt } from './utils/fmt';
import { generateCashflowInterpretation } from './utils/interpretations';

function WfRow({ label, amount, maxAbs, isResult, isDashed, tag }) {
  const barPct = maxAbs > 0 ? Math.abs(amount) / maxAbs * 100 : 0;
  const positive = amount >= 0;
  const rowH = isResult ? 44 : 36;
  const valueSize = isResult ? 16 : 14;
  const valueFw = isResult ? 600 : 400;
  const valueColor = isResult
    ? (positive ? '#1FA534' : '#E83838')
    : (positive ? '#1FA534' : '#5D5D6D');
  const labelSize = isResult ? 16 : 14;
  const labelColor = isResult ? '#1F1F1F' : '#777888';

  return (
    <div className="flex items-center gap-3" style={{ height: rowH }}>
      <span className="flex-1 flex items-center gap-1.5" style={{ fontSize: labelSize, color: labelColor, fontWeight: isResult ? 600 : 400 }}>
        {label}
        {tag && (
          <span className="text-[11px] font-semibold rounded px-1.5 py-0.5" style={{ background: '#E8F7F7', color: '#0E7A6B', borderRadius: 4 }}>
            {tag}
          </span>
        )}
      </span>
      <span
        style={{ width: 88, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: valueSize, fontWeight: valueFw, color: valueColor }}
      >
        {positive ? '+' : ''}{fmt(amount)} €
      </span>
      <div className="shrink-0 rounded-full overflow-hidden" style={{ width: 100, height: 12 }}>
        <div
          className={`h-full rounded-full ${isDashed ? 'opacity-50' : ''}`}
          style={{
            width: `${Math.min(barPct, 100)}%`,
            background: positive ? '#1DB3B3' : (isResult && !positive ? '#E83838' : '#ACACB8'),
            transition: 'width 0.5s ease',
            ...(isDashed ? {
              backgroundImage: 'repeating-linear-gradient(90deg, #1DB3B3 0, #1DB3B3 4px, transparent 4px, transparent 8px)',
              background: undefined,
            } : {}),
          }}
        />
      </div>
    </div>
  );
}

export default function CostWaterfall({ baseline }) {
  const cf = baseline?.cf;
  if (!cf) return null;

  // Bewirtschaftung pauschal 1,00 EUR/m² — konsistent mit Plugin und dealCalc
  const bewirtschaftung = cf.bewirtschaftung ?? Math.round((baseline?.wohnflaeche || 0) * 1.00);
  const nettomiete = cf.einnahmen - bewirtschaftung;
  // Hauptcashflow = Cashflow (vor Tilgung)
  const cashflow = cf.cashflow ?? (nettomiete - cf.davonZins);
  // Cashflow nach Tilgung: nur für Simulationsdetail in Waterfall
  const cfNachTilgung = cf.cashflowNachTilgung ?? (cashflow - cf.davonTilgung);
  const maxAbs = Math.max(cf.einnahmen, bewirtschaftung + cf.davonZins + cf.davonTilgung, Math.abs(cfNachTilgung), 1);

  const interpretation = generateCashflowInterpretation(cf, baseline);

  return (
    <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E0E0DF' }}>
      <h3 className="text-[20px] font-semibold text-[#1F1F1F] mb-3">Ertragsrechnung</h3>

      <WfRow label="Kaltmiete" amount={cf.einnahmen} maxAbs={maxAbs} tag="Marktmiete" />
      <WfRow label="− Bewirtschaftung" amount={-bewirtschaftung} maxAbs={maxAbs} />

      <div className="border-t border-dashed my-1" style={{ borderColor: '#E0E0DF' }} />
      <WfRow label="= Nettomiete" amount={nettomiete} maxAbs={maxAbs} isResult />

      <WfRow label="− Zinsen" amount={-cf.davonZins} maxAbs={maxAbs} />

      <div className="border-t border-dashed my-1" style={{ borderColor: '#E0E0DF' }} />
      <WfRow label="= Cashflow" amount={cashflow} maxAbs={maxAbs} isResult />

      <WfRow label="− Tilgung" amount={-cf.davonTilgung} maxAbs={maxAbs} />

      <div className="border-t-2 my-1" style={{ borderColor: '#C8C8D0' }} />
      <WfRow label="= CF inkl. Tilgung" amount={cfNachTilgung} maxAbs={maxAbs} isResult tag="Simulation" />

      <WfRow label="Vermögensaufbau" amount={cf.davonTilgung} maxAbs={maxAbs} isDashed />

      {interpretation && (
        <div className="mt-3 rounded-md p-3 flex items-start gap-2" style={{ background: '#E8F7F7' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" style={{ color: '#1DB3B3' }}>
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 11a.75.75 0 110-1.5.75.75 0 010 1.5zm.75-4H7.25V4.5h1.5V8z" fill="currentColor"/>
          </svg>
          <p className="text-[13px] leading-[1.5]" style={{ color: '#5D5D6D' }}>{interpretation}</p>
        </div>
      )}
    </div>
  );
}
